import React, { useEffect, useMemo, useState } from "react"

import { addDays, format, isSameDay } from "date-fns"

import Icon from "@/components/Icon"
import {
  ListViewShiftSection,
  ShiftSubGroupRow,
} from "@/components/sheriff/ui/ListViewShiftSection"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import { Card, CardContent } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Spinner } from "@/components/ui/spinner"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import AssignVehicleDialog from "@/components/vehicles/AssignVehicleDialog"
import type { CandidateShift } from "@/hooks/useCandidateVehicles"
import { useDepots } from "@/hooks/useDepots"
import { useShifts } from "@/hooks/useShifts"
import { ukDayEndOf, ukDayStartOf } from "@/lib/ukCalendarDate"
import { ukFormat } from "@/lib/ukFormat"
import type { Shift } from "@/services/shiftsService"

import type { Enums } from "../../../database.types"

// The areas a vehicle is assigned for, matching the delivery schedule view's own list.
const DELIVERY_AREAS = ["Rc1 Deliveries", "Rc2 Deliveries"]

function toCandidateShift(shift: Shift, depotId: string | null): CandidateShift {
  return {
    id: shift.id,
    start_time: shift.startTime,
    end_time: shift.endTime,
    depot_id: depotId,
    type: shift.type as Enums<"person_type">,
    vehicleId: shift.vehicle?.id ?? null,
    stickyLocation: shift.people?.stickyLocation ?? null,
  }
}

interface VehicleAssignmentContentProps {
  selectedDate: Date
  onSelectedDateChange: (date: Date) => void
}

export default function VehicleAssignmentTab({
  selectedDate,
  onSelectedDateChange,
}: VehicleAssignmentContentProps) {
  const [location, setLocation] = useState<string>("")
  const [type, setType] = useState("all")
  const [searchTerm, setSearchTerm] = useState("")
  const [expandedAreas, setExpandedAreas] = useState<Record<string, boolean>>({})
  const [assigning, setAssigning] = useState<Shift | null>(null)

  const { data: depots = [] } = useDepots()

  // shifts.location holds the depot name, so the depot list is the location list.
  const locations = useMemo(() => depots.map((depot) => depot.name).sort(), [depots])

  // A depot at a time, as the schedule views do - useShifts stays disabled until one is picked.
  useEffect(() => {
    if (!location && locations.length > 0) setLocation(locations[0])
  }, [location, locations])

  const depotIdByName = useMemo(
    () => new Map(depots.map((depot) => [depot.name, depot.id])),
    [depots],
  )

  const {
    data: shifts = [],
    isLoading,
    error,
  } = useShifts({
    startTime: ukDayStartOf(selectedDate).toISOString(),
    endTime: ukDayEndOf(selectedDate).toISOString(),
    location: location || undefined,
    areas: DELIVERY_AREAS,
    pageSize: 500,
  })

  const types = useMemo(() => [...new Set(shifts.map((s) => s.type))].sort(), [shifts])

  const visible = useMemo(() => {
    const term = searchTerm.trim().toLowerCase()
    return shifts
      .filter((s) => s.status === "Filled")
      .filter((s) => type === "all" || s.type === type)
      .filter(
        (s) =>
          !term ||
          s.people?.person?.toLowerCase().includes(term) ||
          s.vehicle?.reg?.toLowerCase().includes(term) ||
          s.id.includes(term),
      )
  }, [shifts, type, searchTerm])

  // Two groups, then by area within each. Blocks still needing a vehicle lead, since they are the
  // work this view exists for.
  const groups = useMemo(() => {
    const buckets: [string, Shift[]][] = [
      ["Unassigned", visible.filter((s) => !s.vehicle?.id)],
      ["Assigned", visible.filter((s) => s.vehicle?.id)],
    ]

    return buckets.map(([key, rows]) => {
      const byArea = new Map<string, Shift[]>()
      for (const shift of rows) {
        const area = shift.area || "Unknown"
        if (!byArea.has(area)) byArea.set(area, [])
        byArea.get(area)!.push(shift)
      }
      for (const list of byArea.values()) {
        list.sort((a, b) => a.startTime.localeCompare(b.startTime))
      }
      return {
        key,
        shifts: rows,
        areas: [...byArea.entries()].sort((a, b) => a[0].localeCompare(b[0])),
      }
    })
  }, [visible])

  const sections = groups.filter((group) => group.shifts.length > 0)

  const toggleArea = (vehicle: string, area: string) =>
    setExpandedAreas((previous) => {
      const key = `${vehicle}__${area}`
      return { ...previous, [key]: previous[key] === false }
    })

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardContent className="flex flex-wrap items-end gap-4 p-4">
          <div className="space-y-2">
            <Label>Date</Label>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="icon"
                aria-label="Previous day"
                onClick={() => onSelectedDateChange(addDays(selectedDate, -1))}
              >
                <Icon name="caretLeft" className="h-4 w-4 text-current" />
              </Button>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-[200px] justify-start">
                    <Icon name="calendarBlank" className="mr-2 h-4 w-4 text-current" />
                    {format(selectedDate, "EEE, d MMM yyyy")}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={(date) => date && onSelectedDateChange(date)}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
              <Button
                variant="outline"
                size="icon"
                aria-label="Next day"
                onClick={() => onSelectedDateChange(addDays(selectedDate, 1))}
              >
                <Icon name="caretRight" className="h-4 w-4 text-current" />
              </Button>
              {!isSameDay(selectedDate, new Date()) && (
                <Button variant="ghost" size="sm" onClick={() => onSelectedDateChange(new Date())}>
                  Today
                </Button>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="assignment-location">Location</Label>
            <Select value={location} onValueChange={setLocation}>
              <SelectTrigger id="assignment-location" className="w-[200px]">
                <SelectValue placeholder="Select location" />
              </SelectTrigger>
              <SelectContent>
                {locations.map((option) => (
                  <SelectItem key={option} value={option}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="assignment-type">Block type</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger id="assignment-type" className="w-[180px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                {types.map((option) => (
                  <SelectItem key={option} value={option}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <div className="bg-white border border-palette-jungleGreen200 rounded-lg">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold text-palette-jungleGreen900">
            Vehicle Assignment ({visible.length})
          </h2>
          <div className="relative flex items-center gap-2">
            <Icon
              name="magnifyingGlass"
              className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 transform text-palette-jungleGreen500 text-current"
            />
            <Input
              className="w-full pl-10 sm:w-[240px]"
              placeholder="Search by driver, vehicle or ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <div className="p-4">
          {isLoading ? (
            <div className="flex justify-center py-12">
              <Spinner size="lg" />
            </div>
          ) : error ? (
            <Alert variant="destructive">
              <Icon name="warningCircleFilled" className="h-4 w-4" />
              <AlertDescription>
                Error loading blocks: {error instanceof Error ? error.message : "Unknown error"}
              </AlertDescription>
            </Alert>
          ) : sections.length === 0 ? (
            <div className="py-16 text-center text-palette-jungleGreen500">
              <p className="text-lg">No delivery blocks found for this date</p>
            </div>
          ) : (
            <div className="space-y-6">
              {sections.map((group) => (
                <ListViewShiftSection key={group.key} title={group.key} count={group.shifts.length}>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[200px]">Driver</TableHead>
                        <TableHead className="w-[140px]">Vehicle</TableHead>
                        <TableHead className="w-[120px]">Start</TableHead>
                        <TableHead className="w-[120px]">End</TableHead>
                        <TableHead className="w-[120px]">Type</TableHead>
                        <TableHead className="w-[150px]">Location</TableHead>
                        <TableHead className="w-[160px]" />
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {group.areas.map(([area, areaShifts]) => {
                        const expanded = expandedAreas[`${group.key}__${area}`] !== false
                        return (
                          <React.Fragment key={area}>
                            <ShiftSubGroupRow
                              label={area}
                              count={areaShifts.length}
                              expanded={expanded}
                              onToggle={() => toggleArea(group.key, area)}
                              colSpan={7}
                            />
                            {expanded &&
                              areaShifts.map((shift) => (
                                <TableRow key={shift.id}>
                                  <TableCell>{shift.people?.person ?? "Unfilled"}</TableCell>
                                  <TableCell>
                                    {shift.vehicle?.reg ?? (
                                      <span className="text-palette-jungleGreen500">-</span>
                                    )}
                                  </TableCell>
                                  <TableCell>{ukFormat(shift.startTime, "HH:mm")}</TableCell>
                                  <TableCell>{ukFormat(shift.endTime, "HH:mm")}</TableCell>
                                  <TableCell>{shift.type}</TableCell>
                                  <TableCell>{shift.location}</TableCell>
                                  <TableCell className="text-right">
                                    <Button
                                      size="sm"
                                      variant={shift.vehicle?.id ? "outline" : "default"}
                                      onClick={() => setAssigning(shift)}
                                    >
                                      {shift.vehicle?.id ? "Change vehicle" : "Assign vehicle"}
                                    </Button>
                                  </TableCell>
                                </TableRow>
                              ))}
                          </React.Fragment>
                        )
                      })}
                    </TableBody>
                  </Table>
                </ListViewShiftSection>
              ))}
            </div>
          )}
        </div>
      </div>

      {assigning && (
        <AssignVehicleDialog
          shift={toCandidateShift(assigning, depotIdByName.get(location) ?? null)}
          currentVehicleReg={assigning.vehicle?.reg ?? null}
          open
          onOpenChange={(open) => !open && setAssigning(null)}
        />
      )}
    </div>
  )
}
